import { clsx } from '@alexis/helpers/clsx';
import CheckboxItem from '@pages/FlightSearchResult/components/CheckboxItem';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { translateText } from '@wego/alexis/helpers/translation';

import { convertFilterStateToDataForTracking } from '@helpers/flight';
import { convertFlightSearchResultFarePriceToPrice } from '@helpers/price';

import useFlightSearchResultsDatadog from '@hooks/useFlightSearchResultsDatadog';

import { getLocaleState, getPriceOptionsState, getTranslationsState } from '@redux/selectors';

import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';

import styles from '../FlightFilterCategoryContent.module.scss';

const STOP_CODE_MAP: Record<StopoverCode, number> = {
  DIRECT: 0,
  MORE_THAN_ONE_STOP: 2,
  ONE_STOP: 1,
};

type StopFilterProps = {
  className?: string;
  genzoTrackApplyFilterActionEvent: any;
  genzoTrackApplyFilterSearchView: () => void;
};

const StopsFilter: React.FC<StopFilterProps> = ({
  className,
  genzoTrackApplyFilterActionEvent,
  genzoTrackApplyFilterSearchView,
}) => {
  const { passengerCount, isMultiCity } = useFlightSearchPageParams();
  const { isPerLegSearch } = useFlightPLS();

  const { stopCodesFilter, toggleMultiCitySingleFilter, departLegFilterOption, activeLeg } =
    useFlightSearchFilters();

  const { stopFilterOptions: _stopFilterOptions, exchangeRate } = useFlightSearchResults();

  const locale = useSelector(getLocaleState);
  const translations = useSelector(getTranslationsState);
  const priceOptions = useSelector(getPriceOptionsState);

  const { recordChangeFilterAction } = useFlightSearchResultsDatadog();

  const selectedStopCodesForActiveLeg = useMemo(() => {
    return stopCodesFilter.find((item) => item.leg === activeLeg)?.values || [];
  }, [activeLeg, stopCodesFilter]);

  const stopsFilterOptions = useMemo(() => {
    if (isMultiCity) {
      return (
        departLegFilterOption?.leg?.stops
          .map((stop) => ({
            price: { ...stop.price },
            name: stop.code,
            id: STOP_CODE_MAP[stop.code],
          }))
          .sort((a, b) => a.id - b.id) || []
      );
    }

    const defaultDirectStopFilterOption = {
      id: 0,
      name: 'DIRECT',
      price: undefined,
    };

    if (
      !_stopFilterOptions.find((filter) => filter.id === 0) &&
      selectedStopCodesForActiveLeg.includes(0)
    ) {
      _stopFilterOptions.unshift(defaultDirectStopFilterOption);
    }

    return _stopFilterOptions;
  }, [
    _stopFilterOptions,
    departLegFilterOption?.leg?.stops,
    isMultiCity,
    selectedStopCodesForActiveLeg,
  ]);

  const handleStopClick = (code: number) => {
    recordChangeFilterAction();

    const newFilter = toggleMultiCitySingleFilter({
      activeLeg,
      value: code,
      searchParamKey: FlightSearchResultSearchParams.Stops,
    });

    genzoTrackApplyFilterActionEvent('filter', 'applied', {
      stops: convertFilterStateToDataForTracking(
        newFilter,
        isMultiCity,
        true,
        isPerLegSearch,
        activeLeg,
      ),
    });
    genzoTrackApplyFilterSearchView();
  };

  return (
    <>
      {stopsFilterOptions.length > 0 ? (
        <div className={clsx(styles.categoryContent, className && styles.inDropdown)}>
          {stopsFilterOptions.map((stop) => (
            <CheckboxItem
              className={className}
              key={stop.id}
              isChecked={selectedStopCodesForActiveLeg.includes(stop.id)}
              label={translateText(translations.stop_count, locale, stop.id)}
              exchangeRate={exchangeRate}
              price={
                stop.price
                  ? convertFlightSearchResultFarePriceToPrice(
                      stop.price,
                      passengerCount,
                      priceOptions.showTotalPrice,
                      true,
                    )
                  : undefined
              }
              onCheckboxToggle={() => handleStopClick(stop.id)}
            />
          ))}
        </div>
      ) : null}
    </>
  );
};

export default StopsFilter;
